import axios from 'axios';
import { errorInteractions } from '../../analytics';
import { cabinetdesignsTransformer } from './utils';

/**
 * Load a project (name + all of its designs).
 *
 * The /Project/{projectId} endpoint responds with project metadata and its
 * designs as siblings: { Project: { Id, Name, RoomType, ... }, Designs: [...] }.
 * The Designs array is normalized so the rest of the app keeps receiving a flat
 * list of designs, and the project Name is surfaced (from the nested Project
 * object) so callers can populate the project title.
 *
 * @param {string} host - API host
 * @param {string} projectId - Project ID
 * @returns {Promise<{name: (string|null), designs: Object[]}|null>}
 *   Project name and transformed designs, or null on error
 */
export async function loadDesignsByProject(host, projectId) {
  const errorMsg = 'Load Designs by Project: Request failed';
  const cacheBustParam = `t=${Date.now()}`;
  try {
    const response = await axios.get(
      `${host}/api/modulardesigner/Project/${projectId}?${cacheBustParam}`
    );
    if (response?.status === 200) {
      const data = response?.data ?? {};
      const designs = cabinetdesignsTransformer(data.Designs ?? []);
      return { name: data.Project?.Name ?? null, designs };
    }
    throw new Error(errorMsg);
  } catch (err) {
    const errorObject = {
      type: 'catastrophic',
      displayType: 'inline',
      pageSection: 'Load Designs by Project',
      message: errorMsg
    };
    const trackErrorInteractions = { errorObject, primaryCategory: 'error', eventName: 'post page error' };
    errorInteractions(trackErrorInteractions);
    return null;
  }
}

/**
 * Load multiple designs with project support information
 * @param {string} host - API host
 * @param {string[]} designIds - Array of design IDs
 * @param {string} designType - Design type (e.g., 'Cabinet')
 * @returns {Promise<Object|null>} Designs with project metadata or null on error
 */
export async function loadDesignsWithProjectSupport(host, designIds, designType = 'Cabinet') {
  const errorMsg = 'Load Designs with Project Support: Request failed';
  try {
    const response = await axios({
      method: 'POST',
      url: `${host}/api/modulardesigner/design/detaillist-with-project-support`,
      data: JSON.stringify({
        ids: designIds,
        designType
      }),
      headers: { 'Content-Type': 'application/json;charset=UTF-8' }
    });
    if (response?.status === 200) {
      return response.data;
    }
    throw new Error(errorMsg);
  } catch (err) {
    const errorObject = {
      type: 'catastrophic',
      displayType: 'inline',
      pageSection: 'Load Designs with Project Support',
      message: errorMsg
    };
    const trackErrorInteractions = { errorObject, primaryCategory: 'error', eventName: 'post page error' };
    errorInteractions(trackErrorInteractions);
    return null;
  }
}

/**
 * Helper function to generate a dummy project ID from style choice
 * @param {number} styleChoiceId - Style choice ID
 * @returns {string} Generated project ID
 */
export const generateProjectId = (styleChoiceId) => {
  return `project-${styleChoiceId}`;
};

/**
 * Helper function to extract style choice ID from project ID
 * @param {string} projectId - Project ID
 * @returns {number|null} Style choice ID or null if invalid
 */
export const extractStyleChoiceFromProjectId = (projectId) => {
  const match = projectId.match(/^project-(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
};
